import { useState, useEffect } from 'react';
import { Check, ChevronDown, Network, UserPlus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import type { SignalApplyOperation } from '@momentum/types';
import { SignalApplicabilityScope } from '@momentum/types';

interface ApplicabilityScopeSectionProps {
  currentApplyOperation: SignalApplyOperation | undefined;
  isActive: boolean;
  isManager: boolean;
  directReportsCount: number;
  fullHierarchyCount: number;
  onApplyScope: (scope: SignalApplicabilityScope) => void;
  onChangeScope: (newScope: SignalApplicabilityScope) => void;
  isApplying: boolean;
}

const SCOPE_DOT_COLORS = {
  [SignalApplicabilityScope.DIRECT_REPORTS]: 'bg-[#9333ea]',
  [SignalApplicabilityScope.FULL_HIERARCHY]: 'bg-[#f59e0b]',
  [SignalApplicabilityScope.SELF]: 'bg-[#2563eb]',
} as const;

export function ApplicabilityScopeSection({
  currentApplyOperation,
  isActive,
  isManager,
  directReportsCount,
  fullHierarchyCount,
  onApplyScope,
  onChangeScope,
  isApplying,
}: ApplicabilityScopeSectionProps) {
  const { t } = useTranslation('knowledge');
  const [applyOpen, setApplyOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  // Optimistic scope: immediately reflects the user's choice.
  // Resets to null when server data arrives (prop changes).
  const [optimisticScope, setOptimisticScope] =
    useState<SignalApplicabilityScope | null>(null);

  const serverScope = currentApplyOperation?.scope ?? null;

  useEffect(() => {
    setOptimisticScope(null);
  }, [serverScope]);

  const effectiveScope = optimisticScope ?? serverScope;

  if (!isManager || !isActive) return null;

  const scopeOptions = [
    {
      value: SignalApplicabilityScope.DIRECT_REPORTS,
      label: t('scopeDirectReportsActivate'),
      description: t('scopeDirectReportsActivateDesc'),
      count: directReportsCount,
      icon: Users,
      iconBg: 'bg-[#faf5ff]',
      iconColor: 'text-[#9333ea]',
    },
    {
      value: SignalApplicabilityScope.FULL_HIERARCHY,
      label: t('scopeFullHierarchyActivate'),
      description: t('scopeFullHierarchyActivateDesc'),
      count: fullHierarchyCount,
      icon: Network,
      iconBg: 'bg-[#fef3c7]',
      iconColor: 'text-[#d97706]',
    },
  ];

  const handleApply = (scope: SignalApplicabilityScope) => {
    setOptimisticScope(scope);
    onApplyScope(scope);
  };

  const handleChange = (newScope: SignalApplicabilityScope) => {
    setOptimisticScope(newScope);
    onChangeScope(newScope);
  };

  // Hide loading when we optimistically switched to SELF
  const showApplyLoading =
    isApplying && optimisticScope !== SignalApplicabilityScope.SELF;

  // Not applied yet — dashed section with blue icon + Apply button
  if (!effectiveScope || effectiveScope === SignalApplicabilityScope.SELF) {
    return (
      <Popover open={applyOpen} onOpenChange={setApplyOpen}>
        <PopoverTrigger asChild>
          <div
            className={`mb-4 cursor-pointer overflow-hidden rounded-lg border transition-all duration-200 ${
              applyOpen
                ? 'border-[1.5px] border-[#93b4e8]'
                : 'border-dashed border-muted-foreground/25'
            }`}
          >
            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-[#2563eb]">
                <UserPlus className="h-3.5 w-3.5" />
              </div>
              <span className="flex-1 text-[13px] text-muted-foreground">
                {applyOpen
                  ? t('detailScopeApplyToTeam')
                  : t('detailScopeApplyPrompt')}
              </span>
              <span
                aria-hidden
                className={`shrink-0 rounded-md border border-[#dbeafe] bg-[#eff6ff] px-3 py-1 text-[12px] font-medium text-[#2563eb] transition-colors hover:bg-[#dbeafe] ${
                  showApplyLoading ? 'opacity-50' : ''
                } ${
                  applyOpen
                    ? 'pointer-events-none invisible h-0 overflow-hidden border-0 p-0'
                    : ''
                }`}
              >
                {showApplyLoading
                  ? t('detailScopeApplying')
                  : t('detailScopeApply')}
              </span>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          className="animate-in fade-in-0 zoom-in-95 p-1"
          style={{
            width: 'var(--radix-popover-trigger-width)',
          }}
        >
          {scopeOptions.map((option) => {
            const OptionIcon = option.icon;
            return (
              <button
                key={option.value}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-start transition-colors hover:bg-muted"
                onClick={() => {
                  setApplyOpen(false);
                  handleApply(option.value);
                }}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${option.iconBg} ${option.iconColor}`}
                >
                  <OptionIcon className="h-3.5 w-3.5" />
                </div>
                <span className="flex flex-1 flex-col">
                  <span className="text-[13px] font-medium text-foreground">
                    {option.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {option.description}
                  </span>
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground/60">
                  {t('detailScopeMembers')(option.count)}
                </span>
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    );
  }

  // Applied — inline sentence with hoverable scope trigger
  const scopeLabel =
    effectiveScope === SignalApplicabilityScope.DIRECT_REPORTS
      ? t('appliedToDirectReports')
      : t('appliedToFullHierarchy');
  const memberCount =
    effectiveScope === SignalApplicabilityScope.DIRECT_REPORTS
      ? directReportsCount
      : fullHierarchyCount;

  return (
    <div className="mb-4 flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <Users className="h-[13px] w-[13px] shrink-0 text-muted-foreground/60" />
      <span>{t('detailScopeAppliedByYouTo')}</span>

      <Popover open={changeOpen} onOpenChange={setChangeOpen}>
        <PopoverTrigger asChild>
          <button
            disabled={isApplying}
            className={`group/scope inline-flex cursor-pointer items-center rounded py-0.5 font-medium text-foreground transition-all duration-150 hover:bg-[#faf5ff] hover:px-1.5 hover:text-[#7e22ce] disabled:opacity-50 ${
              changeOpen ? 'bg-[#faf5ff] px-1.5 text-[#7e22ce]' : 'px-0'
            }`}
          >
            {scopeLabel}
            <ChevronDown
              className={`h-2.5 overflow-hidden text-[#a855f7] transition-all duration-150 group-hover/scope:ms-[3px] group-hover/scope:w-2.5 group-hover/scope:opacity-100 ${
                changeOpen
                  ? 'ms-[3px] w-2.5 rotate-180 opacity-100'
                  : 'ms-0 w-0 opacity-0'
              }`}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-60 animate-in fade-in-0 zoom-in-95 p-1"
        >
          {scopeOptions.map((option) => {
            const isSelected = effectiveScope === option.value;
            return (
              <button
                key={option.value}
                className={`flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-[7px] text-start transition-colors hover:bg-muted ${
                  isSelected ? 'bg-[#faf5ff]' : ''
                }`}
                onClick={() => {
                  if (!isSelected) {
                    setChangeOpen(false);
                    handleChange(option.value);
                  }
                }}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                    SCOPE_DOT_COLORS[option.value]
                  }`}
                />
                <span className="flex-1 text-[13px] text-foreground">
                  {option.label}
                </span>
                <span className="text-[11px] text-muted-foreground/60">
                  {t('detailScopeMembers')(option.count)}
                </span>
                {isSelected && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-[#9333ea]" />
                )}
              </button>
            );
          })}
          <button
            className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-[7px] text-start transition-colors hover:bg-muted"
            onClick={() => {
              setChangeOpen(false);
              handleChange(SignalApplicabilityScope.SELF);
            }}
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                SCOPE_DOT_COLORS[SignalApplicabilityScope.SELF]
              }`}
            />
            <span className="flex-1 text-[13px] text-foreground">
              {t('detailScopeOnlyMe')}
            </span>
          </button>
        </PopoverContent>
      </Popover>

      <span className="text-[12px] text-muted-foreground/60">
        ({t('detailScopeMembers')(memberCount)})
      </span>
    </div>
  );
}
